import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowRight } from 'lucide-react';
import type { JSX } from 'react';
import { PageSection } from '@/components/layout/page.js';
import { ErrorState } from '@/components/ui/error-state.js';
import { ApiError } from '@/lib/api/client.js';
import { fetchFulfillmentSettings, fulfillmentQueryKeys } from './api-martelet.js';
import { IgnoreRulesSection } from './ignore-rules-section.js';
import { MarteletConnectionSection } from './martelet-connection-section.js';
import { MarteletTrackingSection } from './martelet-tracking-section.js';
import { SettingsSectionSkeleton } from './settings-parts.js';

/**
 * Onglet **Martelet** (`02` §12.3), refonte du 10/09/2026 : tout ce qui
 * concerne le centre logistique au même endroit — connexion FTP, Suivi
 * Martelet et ses commandes à ignorer, nom du transporteur affiché au client,
 * suspension des envois.
 *
 * Avant, ces réglages étaient répartis entre trois onglets (« Martelet FTP »,
 * « Maintenance ») et un écran à part (« Expéditions ») : un même sujet, trois
 * endroits où chercher.
 */
export function MarteletTab(): JSX.Element {
  const settingsQuery = useQuery({
    queryKey: fulfillmentQueryKeys.settings(),
    queryFn: fetchFulfillmentSettings,
  });

  return (
    <div className="flex flex-col gap-6">
      {settingsQuery.isPending ? (
        <SettingsSectionSkeleton fields={2} />
      ) : settingsQuery.isError ? (
        <PageSection title="Suivi Martelet">
          <ErrorState
            message={
              settingsQuery.error instanceof ApiError
                ? settingsQuery.error.message
                : 'Impossible de charger les réglages du Suivi Martelet.'
            }
            detail={
              settingsQuery.error instanceof ApiError ? settingsQuery.error.detail : undefined
            }
            onRetry={() => {
              void settingsQuery.refetch();
            }}
            className="border-0 bg-transparent py-6"
          />
        </PageSection>
      ) : (
        <MarteletTrackingSection settings={settingsQuery.data} />
      )}

      <IgnoreRulesSection />

      <MarteletConnectionSection />

      <p className="text-sm">
        <Link
          to="/fichiers"
          search={{ page: 1, pageSize: 50, sortDir: 'desc' as const }}
          className="inline-flex items-center gap-1.5 rounded-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          Historique des fichiers envoyés
          <ArrowRight className="size-4" aria-hidden="true" />
        </Link>
      </p>
    </div>
  );
}
